import React from 'react';
import { parseNumber } from '../utils';
import { DEFAULT_EMBED_SETTINGS, normalizeEmbedSettings } from '../../../utils/embedSnippet';

const colorInputValue = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;

const SHADOW_CONTROL_OPTIONS = [
    { label: 'None', value: 'none' },
    { label: 'Subtle', value: '0 1px 2px rgba(0,0,0,0.06)' },
    { label: 'Soft', value: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)' },
    { label: 'Medium', value: '0 10px 15px -3px rgba(0,0,0,0.12), 0 4px 6px -4px rgba(0,0,0,0.08)' },
    { label: 'Strong', value: '0 18px 28px -8px rgba(0,0,0,0.22), 0 8px 12px -8px rgba(0,0,0,0.12)' }
];

const CHECKERBOARD = 'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)';

function FieldLabel({ children }) {
    return (
        <label style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
        }}>
            {children}
        </label>
    );
}

function SectionHeader({ title, description }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <h4 style={{
                fontSize: '0.8rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-secondary)',
                margin: 0
            }}>
                {title}
            </h4>
            {description && (
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
                    {description}
                </span>
            )}
        </div>
    );
}

function ColorControl({ label, value, fallback, onChange }) {
    const isTransparent = value === 'transparent';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{
                    position: 'relative',
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    border: '1px solid var(--border)',
                    background: isTransparent ? CHECKERBOARD : value,
                    backgroundSize: isTransparent ? '10px 10px' : undefined,
                    backgroundPosition: isTransparent ? '0 0, 0 5px, 5px -5px, -5px 0px' : undefined,
                    overflow: 'hidden',
                    flexShrink: 0
                }}>
                    <input
                        type="color"
                        value={colorInputValue(value, fallback)}
                        onChange={(e) => onChange(e.target.value)}
                        style={{
                            position: 'absolute',
                            top: '-5px',
                            left: '-5px',
                            width: '46px',
                            height: '46px',
                            border: 0,
                            padding: 0,
                            opacity: isTransparent ? 0 : 1,
                            cursor: 'pointer'
                        }}
                    />
                </div>
                <input
                    type="text"
                    className="input-field"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        margin: 0,
                        padding: '0.4rem 0.6rem',
                        fontSize: '0.825rem',
                        height: '36px',
                        boxSizing: 'border-box'
                    }}
                    placeholder={fallback}
                />
            </div>
        </div>
    );
}

function RangeTextControl({ label, value, min, max, onChange, formatValue }) {
    const sliderValue = Math.min(max, Math.max(min, parseNumber(value)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                <FieldLabel>{label}</FieldLabel>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{value}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={sliderValue}
                    onChange={(e) => {
                        const nextValue = Number.parseInt(e.target.value, 10);
                        onChange(formatValue ? formatValue(nextValue) : `${nextValue}px`);
                    }}
                    style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                />
                <input
                    type="text"
                    className="input-field"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        width: '76px',
                        margin: 0,
                        padding: '0.3rem 0.5rem',
                        fontSize: '0.8rem',
                        height: '30px',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                    }}
                />
            </div>
        </div>
    );
}

function parseSize(value, defaultUnit = 'px') {
    const match = String(value || '').trim().match(/^(\d+(?:\.\d+)?)(px|%)?$/i);
    if (!match) return { amount: '', unit: defaultUnit };
    return { amount: match[1], unit: (match[2] || defaultUnit).toLowerCase() };
}

function SizeControl({ label, value, onChange, units = ['px', '%'], defaultUnit = 'px' }) {
    const { amount, unit } = parseSize(value, defaultUnit);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                    type="number"
                    min="0"
                    className="input-field"
                    value={amount}
                    onChange={(e) => onChange(`${e.target.value}${unit}`)}
                    style={{
                        margin: 0,
                        padding: '0.4rem 0.6rem',
                        fontSize: '0.825rem',
                        height: '36px',
                        boxSizing: 'border-box',
                        flex: 1
                    }}
                />
                <select
                    className="input-field"
                    value={unit}
                    onChange={(e) => onChange(`${amount || 0}${e.target.value}`)}
                    style={{
                        margin: 0,
                        padding: '0.4rem 0.5rem',
                        fontSize: '0.825rem',
                        height: '36px',
                        width: '72px'
                    }}
                >
                    {units.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}

function CheckboxControl({ label, checked, onChange }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
                type="checkbox"
                checked={Boolean(checked)}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {label}
            </span>
        </label>
    );
}

export default function StyleControlsPanel({
    manualStyles,
    onStyleChange,
    embed = DEFAULT_EMBED_SETTINGS,
    onEmbedChange = () => {}
}) {
    const embedSettings = normalizeEmbedSettings(embed);
    const pageBackgroundValue = embedSettings.transparent ? 'transparent' : embedSettings.pageBackground;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <SectionHeader
                title="Look"
                description="Colors, type, and surfaces used by the live configurator."
            />

            <ColorControl
                label="Page background"
                value={pageBackgroundValue}
                fallback="#ffffff"
                onChange={(value) => onEmbedChange({
                    pageBackground: value === 'transparent' ? embedSettings.pageBackground : value,
                    transparent: value === 'transparent'
                })}
            />
            <CheckboxControl
                label="Transparent canvas"
                checked={embedSettings.transparent}
                onChange={(checked) => onEmbedChange('transparent', checked)}
            />

            <ColorControl
                label="Accent"
                value={manualStyles.primaryColor}
                fallback="#2563eb"
                onChange={(value) => onStyleChange('primaryColor', value)}
            />

            <ColorControl
                label="Card/Input Background"
                value={manualStyles.backgroundColor}
                fallback="#ffffff"
                onChange={(value) => onStyleChange('backgroundColor', value)}
            />

            <ColorControl
                label="Text"
                value={manualStyles.textColor}
                fallback="#1f2937"
                onChange={(value) => onStyleChange('textColor', value)}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                <FieldLabel>Font</FieldLabel>
                <select
                    className="input-field"
                    value={manualStyles.fontFamily}
                    onChange={(e) => onStyleChange('fontFamily', e.target.value)}
                    style={{ fontSize: '0.825rem', padding: '0.5rem', margin: 0, height: '36px' }}
                >
                    <option value="Inter">Inter</option>
                    <option value="system-ui">System UI</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Playfair Display">Playfair Display</option>
                </select>
            </div>

            <RangeTextControl
                label="Border Radius"
                value={manualStyles.borderRadius}
                min={0}
                max={30}
                onChange={(value) => onStyleChange('borderRadius', value)}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                <FieldLabel>Shadow</FieldLabel>
                <select
                    className="input-field"
                    value={manualStyles.shadow}
                    onChange={(e) => onStyleChange('shadow', e.target.value)}
                    style={{ fontSize: '0.825rem', padding: '0.5rem', margin: 0, height: '36px' }}
                >
                    {SHADOW_CONTROL_OPTIONS.map(option => (
                        <option key={option.label} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>

            <RangeTextControl
                label="Input Padding"
                value={manualStyles.inputPadding}
                min={4}
                max={24}
                onChange={(value) => onStyleChange('inputPadding', value)}
                formatValue={(value) => `${value}px ${Math.round(value * 1.33)}px`}
            />

            <RangeTextControl
                label="Button Padding"
                value={manualStyles.buttonPadding}
                min={6}
                max={28}
                onChange={(value) => onStyleChange('buttonPadding', value)}
                formatValue={(value) => `${value}px ${Math.round(value * 2)}px`}
            />

            <RangeTextControl
                label="Feature Spacing"
                value={manualStyles.groupSpacing}
                min={8}
                max={48}
                onChange={(value) => onStyleChange('groupSpacing', value)}
            />

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <SectionHeader
                    title="Embed frame"
                    description="Default iframe and widget size used in copied snippets and View preview."
                />
            </div>

            <SizeControl
                label="Width"
                value={embedSettings.width}
                onChange={(value) => onEmbedChange('width', value)}
                units={['%', 'px']}
                defaultUnit="%"
            />

            <SizeControl
                label="Height"
                value={embedSettings.height}
                onChange={(value) => onEmbedChange('height', value)}
                units={['px', '%']}
                defaultUnit="px"
            />

            <SizeControl
                label="Max width"
                value={embedSettings.maxWidth || ''}
                onChange={(value) => onEmbedChange('maxWidth', parseNumber(value) ? `${parseNumber(value)}px` : '')}
                units={['px']}
                defaultUnit="px"
            />

            <CheckboxControl
                label="Show iframe border"
                checked={embedSettings.showBorder}
                onChange={(checked) => onEmbedChange('showBorder', checked)}
            />

            {embedSettings.showBorder && (
                <ColorControl
                    label="Iframe border"
                    value={embedSettings.borderColor}
                    fallback="#e5e7eb"
                    onChange={(value) => onEmbedChange('borderColor', value)}
                />
            )}

            <RangeTextControl
                label="Iframe radius"
                value={embedSettings.borderRadius}
                min={0}
                max={24}
                onChange={(value) => onEmbedChange('borderRadius', value)}
            />

            <RangeTextControl
                label="Content padding"
                value={embedSettings.contentPadding}
                min={8}
                max={80}
                onChange={(value) => onEmbedChange('contentPadding', value)}
            />
        </div>
    );
}
